import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Cropper, { type Area } from 'react-easy-crop';
import { api, ApiClientError } from '../lib/api.js';

/**
 * Banner uploader con cropper modal y normalización a JPG en el cliente.
 *
 * Flow:
 *   1. El usuario elige cualquier `image/*` (excepto SVG por seguridad).
 *   2. Decodificamos respetando EXIF (`imageOrientation: 'from-image'`) y
 *      mostramos un modal con react-easy-crop bloqueado a 16:9.
 *   3. Al confirmar, dibujamos en un canvas con downscale a `MAX_OUTPUT_WIDTH`
 *      y exportamos como `image/jpeg` con calidad 0.85.
 *   4. Subimos el Blob resultante con PUT presigned URL como `image/jpeg`.
 *
 * Por qué convertir TODO a JPG del lado cliente:
 *   - Un PNG de 4 MB tipico (transparencia que el usuario igual no necesita
 *     en un banner) cae a ~150-250 KB en JPG 1920x1080.
 *   - El bucket S3 se mantiene chico y CloudFront cachea menos bytes.
 *   - El contract con el backend queda simple: para banners siempre llega
 *     `image/jpeg`; nada de detectar formato server-side.
 *
 * Por qué no aceptar SVG: ataques XSS via SVG con <script> embebido (los
 * SVG son XML interpretable). Aunque no los renderizamos como HTML, mejor
 * no abrirles la puerta al pipeline.
 */

interface PresignResponse {
  uploadUrl: string;
  publicUrl: string;
  key: string;
  contentType: string;
  maxBytes: number;
  expiresInSeconds: number;
}

export interface BannerUploaderProps {
  orgId: string;
  /** Current banner URL (controlled). */
  value: string;
  /** Called with the new public URL after a successful upload (or '' on remove). */
  onChange: (url: string) => void;
}

const TARGET_ASPECT = 16 / 9;
const MAX_OUTPUT_WIDTH = 1920;
const JPEG_QUALITY = 0.85;
const MAX_INPUT_BYTES = 20 * 1024 * 1024;

export function BannerUploader({ orgId, value, onChange }: BannerUploaderProps) {
  const { t } = useTranslation('dashboard');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  // Cropper state: el objectURL del file elegido + estado del crop.
  const [editing, setEditing] = useState<{
    objectUrl: string;
    fileName: string;
  } | null>(null);

  // Liberar object URLs al desmontar o al cerrar el modal.
  useEffect(() => {
    return () => {
      if (editing) URL.revokeObjectURL(editing.objectUrl);
    };
  }, [editing]);

  function pick() {
    inputRef.current?.click();
  }

  async function handleFile(file: File) {
    setError(null);
    if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
      setError(t('banner_uploader.format_error'));
      return;
    }
    if (file.size > MAX_INPUT_BYTES) {
      setError(t('banner_uploader.size_error', { mb: MAX_INPUT_BYTES / 1024 / 1024 }));
      return;
    }

    try {
      const objectUrl = URL.createObjectURL(file);
      setEditing({ objectUrl, fileName: file.name });
    } catch {
      setError(t('banner_uploader.read_error'));
    }
  }

  async function confirmCrop(croppedAreaPixels: Area) {
    if (!editing) return;
    setBusy(true);
    setProgress(0);
    setError(null);
    try {
      const blob = await cropAndEncodeJpeg(editing.objectUrl, croppedAreaPixels);
      const presign = await api<PresignResponse>('/uploads/event-banner', {
        method: 'POST',
        body: { orgId, contentType: 'image/jpeg' },
      });
      await uploadWithProgress(presign.uploadUrl, blob, 'image/jpeg', (p) => setProgress(p));
      onChange(presign.publicUrl);
      setProgress(100);
      URL.revokeObjectURL(editing.objectUrl);
      setEditing(null);
    } catch (e) {
      setError(
        e instanceof ApiClientError
          ? e.message
          : e instanceof Error
            ? e.message
            : t('banner_uploader.upload_error'),
      );
    } finally {
      setBusy(false);
    }
  }

  function cancelCrop() {
    if (editing) URL.revokeObjectURL(editing.objectUrl);
    setEditing(null);
    setError(null);
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          e.target.value = '';
        }}
      />

      {value ? (
        <div className="relative group">
          <img
            src={value}
            alt=""
            className="w-full aspect-[16/9] object-cover rounded-lg border border-neutral-800"
          />
          <div className="absolute inset-0 flex items-end p-3 gap-2 bg-gradient-to-t from-black/70 via-black/0 to-black/0 rounded-lg">
            <button
              type="button"
              onClick={pick}
              disabled={busy}
              className="bg-neutral-950/90 text-neutral-100 text-sm px-3 py-2 rounded-md border border-neutral-700 hover:border-neutral-500"
            >
              {busy ? t('banner_uploader.uploading', { progress }) : t('banner_uploader.replace')}
            </button>
            <button
              type="button"
              onClick={() => onChange('')}
              disabled={busy}
              className="bg-neutral-950/70 text-neutral-300 text-sm px-3 py-2 rounded-md border border-neutral-800 hover:text-neutral-100"
            >
              {t('banner_uploader.remove')}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          className="w-full aspect-[16/9] flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-neutral-800 hover:border-neutral-600 text-neutral-500 hover:text-neutral-300 transition disabled:opacity-50"
        >
          {busy ? (
            <>
              <span className="text-sm">{t('banner_uploader.uploading', { progress })}</span>
              <div className="w-2/3 h-1 bg-neutral-800 rounded overflow-hidden">
                <div
                  className="h-full bg-[var(--color-accent)] transition-[width]"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </>
          ) : (
            <>
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="m21 15-5-5L5 21" />
              </svg>
              <span className="text-sm">{t('banner_uploader.upload_banner')}</span>
              <span className="text-xs text-neutral-600">{t('banner_uploader.upload_help')}</span>
            </>
          )}
        </button>
      )}

      {error && <p className="text-red-400 text-sm mt-2">{error}</p>}

      {editing && (
        <CropperModal
          imageSrc={editing.objectUrl}
          onCancel={cancelCrop}
          onConfirm={confirmCrop}
          busy={busy}
          progress={progress}
        />
      )}
    </div>
  );
}

interface CropperModalProps {
  imageSrc: string;
  onCancel: () => void;
  onConfirm: (croppedAreaPixels: Area) => void;
  busy: boolean;
  progress: number;
}

function CropperModal({ imageSrc, onCancel, onConfirm, busy, progress }: CropperModalProps) {
  const { t } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  // Cierre con Escape mientras no estamos subiendo.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('banner_uploader.crop_dialog_aria')}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
    >
      <div className="w-full max-w-3xl bg-neutral-950 border border-neutral-800 rounded-2xl overflow-hidden shadow-2xl">
        <div className="px-5 py-3 border-b border-neutral-900 flex items-center justify-between">
          <h3 className="font-semibold">{t('banner_uploader.crop_title')}</h3>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            aria-label={t('banner_uploader.cancel_aria')}
            className="text-neutral-400 hover:text-neutral-100 disabled:opacity-50"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="relative w-full bg-black" style={{ height: 'min(56vh, 460px)' }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={TARGET_ASPECT}
            cropShape="rect"
            showGrid
            objectFit="contain"
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="px-5 py-4 border-t border-neutral-900 flex flex-col sm:flex-row gap-4 sm:items-center">
          <div className="flex-1 flex items-center gap-3">
            <span className="text-xs uppercase tracking-widest text-neutral-500">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              disabled={busy}
              className="flex-1 accent-[var(--color-accent)]"
              aria-label="Zoom"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="btn-secondary text-sm"
            >
              {tCommon('actions.cancel')}
            </button>
            <button
              type="button"
              onClick={() => croppedAreaPixels && onConfirm(croppedAreaPixels)}
              disabled={busy || !croppedAreaPixels}
              className="btn-primary text-sm"
            >
              {busy
                ? t('banner_uploader.uploading', { progress })
                : t('banner_uploader.crop_and_upload')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Toma el objectURL de la imagen original + el rectángulo de crop en
 * coordenadas-imagen y devuelve un `Blob` JPEG con downscale aplicado.
 *
 * Usamos `createImageBitmap({ imageOrientation: 'from-image' })` para que
 * el browser respete EXIF (típico de fotos de iPhone que vienen rotadas).
 * El fallback con `<img>` se mantiene para Safari viejos.
 */
async function cropAndEncodeJpeg(objectUrl: string, area: Area): Promise<Blob> {
  const bitmap = await loadBitmap(objectUrl);

  const outputWidth = Math.min(area.width, MAX_OUTPUT_WIDTH);
  const outputHeight = Math.round(outputWidth / TARGET_ASPECT);

  const canvas = document.createElement('canvas');
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context not available');

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, outputWidth, outputHeight);

  ctx.drawImage(bitmap, area.x, area.y, area.width, area.height, 0, 0, outputWidth, outputHeight);

  if ('close' in bitmap && typeof bitmap.close === 'function') {
    (bitmap as ImageBitmap).close();
  }

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob returned null'));
      },
      'image/jpeg',
      JPEG_QUALITY,
    );
  });
}

async function loadBitmap(objectUrl: string): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof window.createImageBitmap === 'function') {
    try {
      const response = await fetch(objectUrl);
      const blob = await response.blob();
      return await window.createImageBitmap(blob, { imageOrientation: 'from-image' });
    } catch {
      // Falla silenciosa, intentamos el fallback.
    }
  }
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to decode image'));
    img.src = objectUrl;
  });
}

/**
 * XHR-based PUT con barra de progreso real (fetch no expone upload progress).
 */
function uploadWithProgress(
  url: string,
  body: Blob,
  contentType: string,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 upload failed (${xhr.status})`));
    };
    xhr.send(body);
  });
}
